/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Typography,
  MenuItem,
} from '@mui/material';
import {
  GeneralTable,
  CustomTableCell,
  CustomTableRow,
  ActionTableCell,
  GeneralModal,
  SelectDropdown,
} from '../../index';
import { useState, useEffect } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import { sortByCheckInDate } from '../../../utilities/sortBy';

const subs = {
  fontWeight: '500',
  fontSize: '12px',
  textTransform: 'uppercase',
  textAlign: 'center',
  color: '#828282',
  mb: 4,
};

const totalStyle = {
  display: 'flex',
  height: '2.5rem',
  width: '100%',
  bgcolor: '#84d8c5',
  borderRadius: '5px',
  justifyContent: 'center',
  alignItems: 'center',
  textTransform: 'uppercase',
  fontWeight: '600',
  mb: 2,
};

export const HoursWorkedTable = (props) => {
  const data = props.data;
  const timesheetRange = props.timesheetRange;
  const [entryPerPage, setEntryPerPage] = useState(10);
  const selectedEntryPerPage = 5;
  const workSetup = { 1: 'On-site', 2: 'WFH', 3: 'Deployed' };
  const [slice, setSlice] = useState([0, entryPerPage]);
  const [selectedSlice, setSelectedSlice] = useState([0, selectedEntryPerPage]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [selectedTotalHours, setSelectedTotalHours] = useState(null);

  //Reset to page 1 when changing the number of entries
  useEffect(() => {
    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  }, [entryPerPage, data]);

  // PAGE CHANGE HANDLER
  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    let currPage = page - 1;

    page === 1
      ? setSlice([0, entryPerPage])
      : setSlice([currPage * entryPerPage, page * entryPerPage]);
  };

  const handleSelectedPageChange = (e, page) => {
    let currPage = page - 1;

    page === 1
      ? setSelectedSlice([0, selectedEntryPerPage])
      : setSelectedSlice([
          currPage * selectedEntryPerPage,
          page * selectedEntryPerPage,
        ]);
  };

  const handleViewModalOpen = (event) => {
    const selected = data[event.currentTarget.id];

    setSelectedName(selected.name);
    setSelectedTotalHours(selected.hours);
    let sortedTimesheetData = sortByCheckInDate(
      timesheetRange.filter((time) => time.empid === selected.empid)
    );
    setSelectedTimesheet(sortedTimesheetData);
    setViewOpen(true);
  };

  return (
    <>
      <GeneralTable
        sx={{
          borderRadius: '5px',
          boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
          mb: 3.6,
          display: 'block',
        }}
        length={data.length}
        entryPerPage={entryPerPage}
        page={currentPage}
        pageCount={Math.floor(data.length / entryPerPage)}
        handlePageChange={handlePageChange}
        entriesPerPageComponent={
          <SelectDropdown
            sx={{ minWidth: 80 }}
            size="small"
            label="Entries"
            value={entryPerPage}
            onChange={(e) => setEntryPerPage(e.target.value)}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </SelectDropdown>
        }
      >
        <TableHead>
          <TableRow>
            <CustomTableCell value="Employee ID" />
            <CustomTableCell align="left" value="Employee Name" />
            <CustomTableCell align="left" value="Department" />
            <CustomTableCell align="left" value="Total Hours" />
            <CustomTableCell align="center" value="View" />
          </TableRow>
        </TableHead>
        <TableBody>
          {data.slice(slice[0], slice[1]).map((emp, index) => (
            <CustomTableRow key={emp.empid}>
              <CustomTableCell scope="row" value={emp.empid} />
              <CustomTableCell scope="row" value={emp.name} />
              <CustomTableCell scope="row" value={emp.deptname} />
              <CustomTableCell scope="row" value={emp.hours} />
              <ActionTableCell>
                <IconButton
                  id={
                    (index + entryPerPage) * (currentPage - 1) -
                    (currentPage - 2) * index
                  }
                  size="small"
                  onClick={handleViewModalOpen}
                >
                  <VisibilityIcon fontSize="inherit" />
                </IconButton>
              </ActionTableCell>
            </CustomTableRow>
          ))}
        </TableBody>
      </GeneralTable>
      <GeneralModal
        style={{ width: '50%' }}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
      >
        <Typography
          fontWeight="800"
          variant="h4"
          align="center"
          textTransform={'uppercase'}
        >
          {selectedName}
        </Typography>
        <Typography sx={subs}>
          {(() => {
            if (
              new Date(props.startValue).getDate() ==
              new Date(props.endValue).getDate()
            ) {
              return String(
                'timesheet record for ' +
                  `${format(new Date(props.startValue), 'dd MMM yyy')}`
              );
            } else {
              return String(
                'timesheet record from ' +
                  `${format(new Date(props.startValue), 'dd MMM yyy')}` +
                  ' to ' +
                  `${format(new Date(props.endValue), 'dd MMM yyy')}`
              );
            }
          })()}
        </Typography>
        <Typography
          variant="h6"
          sx={totalStyle}
        >{`${selectedTotalHours} Hours`}</Typography>
        <GeneralTable
          sx={{ borderRadius: '5px', width: '100%', mb: 3 }}
          length={selectedTimesheet.length}
          entryPerPage={selectedEntryPerPage}
          pageCount={Math.floor(
            selectedTimesheet.length / selectedEntryPerPage
          )}
          handlePageChange={handleSelectedPageChange}
        >
          <TableHead>
            <TableRow>
              <CustomTableCell align="left" value="Date" />
              <CustomTableCell align="left" value="Check In" />
              <CustomTableCell align="left" value="Check Out" />
              <CustomTableCell align="left" value="Total Hours" />
              <CustomTableCell align="left" value="Work Setup" />
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedTimesheet
              .slice(selectedSlice[0], selectedSlice[1])
              .map((data, index) => (
                <CustomTableRow key={index}>
                  <CustomTableCell
                    scope="row"
                    value={format(new Date(data.checkin), 'dd MMM yyyy')}
                  />
                  <CustomTableCell
                    scope="row"
                    value={format(new Date(data.checkin), 'hh:mm:ss a')}
                  />
                  <CustomTableCell
                    scope="row"
                    value={format(new Date(data.checkout), 'hh:mm:ss a')}
                  />
                  <CustomTableCell
                    scope="row"
                    value={Math.floor((data.roundedtime / 60) * 1000) / 1000}
                  />
                  <CustomTableCell value={workSetup[data.worksetup]} />
                </CustomTableRow>
              ))}
          </TableBody>
        </GeneralTable>
      </GeneralModal>
    </>
  );
};
